import { NavLink, Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  AUTH_CHANGED_EVENT,
  AUTH_UNAUTHORIZED_EVENT,
  clearAuth,
  getToken,
  getUser,
  refreshAuthSession,
  sanitizeStoredUserForFallback,
  type AuthUser,
} from '@/lib/auth';
import { useSurfaces } from '@/hooks/useSurfaces';
import { NAV_SURFACES } from '@/lib/surfaceManifest';
import { UserMenu } from './UserMenu';
import { DirectorTenantSwitcher } from './DirectorTenantSwitcher';
import { SeityOnboardingGate } from './SeityOnboardingGate';
import { RouteNoticeProvider } from './RouteNotice';

function userFacingAuthMessage(message?: string): string {
  if (!message) return 'Your session expired. Sign in again.';
  return /invalid authentication token/i.test(message)
    ? 'Your session expired. Sign in again.'
    : message;
}

// The utilitarian topbar-only shell. No sidebar, no atmospheric framing.
// Keep information density high, chrome low.
//
// Topbar layout:
//   Left:   Logo / "Seitiate+" wordmark
//   Center: Nav links — Chat | Inbox | Dashboard | Board | Workbench | Skills | Talents | Sources | Research | Settings (+Command for Director)
//   Right:  Director tenant switcher (Director only) | User menu dropdown

export function AppShell() {
  const [user, setUser] = useState<AuthUser | null>(() => getUser());
  const [checkingSession, setCheckingSession] = useState(() => Boolean(getToken() && getUser()));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const syncUser = () => setUser(getUser());
    const onUnauthorized = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      setUser(null);
      navigate('/login', {
        replace: true,
        state: {
          from: `${location.pathname}${location.search}`,
          message: userFacingAuthMessage(detail?.message),
        },
      });
    };

    window.addEventListener(AUTH_CHANGED_EVENT, syncUser);
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, syncUser);
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    };
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    let cancelled = false;
    if (!getToken() || !getUser()) {
      setCheckingSession(false);
      return;
    }
    setCheckingSession(true);
    refreshAuthSession({ allowMissingEndpoint: true })
      .then((freshUser) => {
        if (!cancelled) setUser(freshUser);
      })
      .catch(() => {
        if (!cancelled) setUser(getToken() ? sanitizeStoredUserForFallback(getUser()) : null);
      })
      .finally(() => {
        if (!cancelled) setCheckingSession(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    clearAuth();
    navigate('/login', { replace: true });
  };

  const isDashboardRoute = location.pathname === '/dashboard' || location.pathname === '/';
  // Invite-join routes must NOT sit behind the seity gate: the invitee has no
  // tenant yet (joining is what grants one), so the gate's /seity/status call
  // 403s ("No tenant found for this user") and walls the very page that would
  // fix it. Auth is still enforced by this shell's login redirect below.
  const isJoinRoute = location.pathname.startsWith('/workspaces/join');
  const isAuthenticated = Boolean(user && getToken());
  // Authoritative, backend-resolved surface set (fail-closed). Drives BOTH the
  // nav below and the RequireSurface route guards, so they cannot diverge.
  const { has } = useSurfaces();

  if (!checkingSession && !isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from: `${location.pathname}${location.search}`,
          message: 'Sign in to continue.',
        }}
      />
    );
  }

  if (checkingSession) {
    return (
      <div className="sp-shell">
        <main className="sp-main">
          <div style={{ padding: 24, color: 'var(--sp-text-dim)' }}>Checking session...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="sp-shell">
      <header className="sp-topbar">
        {/* Left: Brand */}
        <span className="sp-brand mono">Seitiate+</span>

        {/* Center: Nav — rendered from the backend-resolved visible-surface set.
            A customer (tenant) sees only the customer-safe surfaces; operator /
            internal / Director links are absent because the backend omits them. */}
        <nav className="sp-nav">
          {NAV_SURFACES.filter((s) => has(s.id)).map((s) => (
            <NavLink
              key={s.id}
              to={s.path}
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              {s.label}
            </NavLink>
          ))}
        </nav>

        {/* Spacer */}
        <div className="sp-topbar-spacer" />

        {/* Right: Tenant switcher (Director) + User menu */}
        {has('tenant_switcher') && <DirectorTenantSwitcher />}
        {user && (
          <UserMenu
            user={user}
            onLogout={handleLogout}
            onNavigateSettings={() => navigate('/settings')}
          />
        )}
      </header>

      {/* Page tab bar is rendered by DashboardPage when on /dashboard route.
          We expose isDashboardRoute so the main area knows whether to suppress padding
          for the tab bar area. */}
      <main className={`sp-main${isDashboardRoute ? ' sp-main--dashboard' : ''}`}>
        <RouteNoticeProvider>
          {isJoinRoute ? (
            <Outlet />
          ) : (
            <SeityOnboardingGate>
              <Outlet />
            </SeityOnboardingGate>
          )}
        </RouteNoticeProvider>
      </main>
    </div>
  );
}
